import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOM_BLOTTER_GROUPING,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  FormControlSizes,
  HStack,
  HelpIcon,
  IconButton,
  IconName,
  MixpanelEvent,
  Text,
  Toggle,
  columnTypes,
  filterExistsAndExcludes,
  parseDate,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterGroupingToggling,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useSyncedRef,
  useWSFilterPipe,
  type BlotterGroupingDefinition,
  type Leaves,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useIncludeCashFilterFunc } from '@src/hooks/useIncludeCashFilter';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import { compact } from 'lodash-es';
import { useFeatureFlag } from '../../../../hooks';
import { useGetBulkClosablePositions } from '../BulkClosePosition/useGetBulkClosablePositions';
import { defaultUnifiedBlotterColumns, defaultUnifiedBlotterFilter } from '../defaults';
import type {
  ClonePositionsTabFunc,
  ExtraPositionColumns,
  ExtraUnifiedPositionColumns,
  UnifiedPositionsTabProps,
} from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import type { UnifiedPosition } from './UnifiedPosition';
import { usePositionsUnifiedColumns } from './usePositionsUnifiedColumns';
import { usePositionsUnifiedFilter } from './usePositionsUnifiedFilter';
import { useUnifiedPositionsBalancesObs } from './useUnifiedPositionsBalancesObs';

export interface PositionsUnifiedBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: UnifiedPositionsTabProps;
  onUpdateTab: (tab: UnifiedPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

function getPositionKey(position: UnifiedPosition) {
  return position.rowID;
}

function getPositionAsset(position: UnifiedPosition) {
  return position.Asset;
}

const aggSpecs = [
  { valuePath: 'Equivalent.Delta', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.UnrealizedPnL', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.Amount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'talosEquityEquivalent', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'balanceEquivalent', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<UnifiedPosition>; currencyPath: Leaves<UnifiedPosition> }[];

type UnifiedGroupings = 'Market' | 'Account' | 'Underlying' | 'Custom';
type GroupablePositionColumns = keyof UnifiedPosition | ExtraPositionColumns | ExtraUnifiedPositionColumns;
const GROUPINGS: BlotterGroupingDefinition<UnifiedGroupings, GroupablePositionColumns>[] = [
  {
    key: 'Market',
    label: 'By Market',
    rowGroupColumns: ['market', 'MarketAccount', 'underlying', 'Asset'],
    mixpanelEvent: MixpanelEvent.GroupAccounts,
    buttonDataTestId: 'by-market-grouping',
  },
  {
    key: 'Account',
    label: 'By Account',
    rowGroupColumns: ['MarketAccount', 'underlying', 'Asset'],
    mixpanelEvent: MixpanelEvent.GroupAccounts,
    buttonDataTestId: 'by-account-grouping',
  },

  {
    key: 'Underlying',
    label: 'By Underlying',
    rowGroupColumns: ['underlying', 'Asset', 'MarketAccount'],
    mixpanelEvent: MixpanelEvent.GroupAccounts,
    buttonDataTestId: 'by-underlying-grouping',
  },
  {
    key: CUSTOM_BLOTTER_GROUPING,
    label: 'Custom',
    rowGroupColumns: [],
    buttonDataTestId: 'custom-grouping',
  },
];

export function PositionsUnifiedBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
  tag,
}: PositionsUnifiedBlotterParams) {
  const { enableBulkClosePosition, enableLegacyUpdateBalances } = useFeatureFlag();
  const mixpanel = useMixpanel();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultUnifiedBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultUnifiedBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const includeCash = tab.includeCash ?? true;

  const { securitiesBySymbol } = useSecuritiesContext();

  const defaultColumns = usePositionsUnifiedColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = usePositionsUnifiedFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu<UnifiedPosition>({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    enableBulkClose: true,
    tabType: 'Unified',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getPositionKey,
    aggSpecs,
  });

  const includeCashFilterFunc = useIncludeCashFilterFunc({
    includeCash,
    assetGetter: getPositionAsset,
  });

  const rxjsFilterFunc = useCallback(
    (position: UnifiedPosition) => {
      // This filtering function just checks the expiry property.
      if (filterResults.filter.Expiries != null) {
        // This resolving will be removed once the Position entity has expiration on it. Backend will add.
        const expiration = securitiesBySymbol.get(position.Asset)?.Expiration;
        if (expiration == null) {
          // We have an expiration filter applied, but the security of this position has no expiry. Exclude it.
          return false;
        }

        const expirationDate = parseDate(expiration);
        const isAllowed = filterResults.filter.Expiries?.some(expiryString => {
          const allowedExpiryDate = parseDate(expiryString);
          return isSameDay(expirationDate, allowedExpiryDate);
        });
        if (!isAllowed) {
          return false;
        }
      }

      if (filterExistsAndExcludes(filterResults.filter, 'MarketAccountGroups', position, 'marketAccountGroup')) {
        return false;
      }

      if (!includeCashFilterFunc(position)) {
        return false;
      }

      return true;
    },
    [securitiesBySymbol, filterResults.filter, includeCashFilterFunc]
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: rxjsFilterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: 300,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const { dataObservable, errorObservable } = useUnifiedPositionsBalancesObs({
    tag: blotterID,
    showZeroBalances,
    filter: filterResults.filter,
  });

  const filteredDataObservable = useMemo(() => dataObservable.pipe(filterPipe), [dataObservable, filterPipe]);

  const blotterTable = useBlotterTable<UnifiedPosition>({
    dataObservable: filteredDataObservable,
    errorObservable,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID' satisfies keyof UnifiedPosition,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      showOpenedGroup: true,
      groupRemoveLowestSingleChildren: true,
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
      getContextMenuItems: positionsMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'PositionsUnified',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions unified',
    setRowGroupColumns,
    groupings: GROUPINGS,
    allowNoGrouping: true,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const { getBulkClosablePositions } = useGetBulkClosablePositions();
  // We make this function stable so we don't do too much work and risk changing the suggested SendOrderArgs returned mid-confirmation.
  const stableGetBulkClosablePositions = useSyncedRef(getBulkClosablePositions);

  const handleCloseAll = useCallbackRef(() => {
    // Important that we get the rows after the aggrid client side filter has been applied.
    // Meaning that we get the rows actually visible to the user.
    const rows = blotterTable.getRowsAfterFilter();
    const positions = compact(rows.map(row => row.data));
    const orderArgsForClosing = stableGetBulkClosablePositions.current(positions);
    positionsMenu.openBulkClosePositionDialog(orderArgsForClosing);
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  const updateIncludeCash = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleUnifiedPositionsIncludeCash);
    onUpdateTab({ ...tab, includeCash: value });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Toggle
                tooltip={CONTROL_TOOLTIPS.includeCash}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={includeCash}
                onChange={updateIncludeCash}
                label="Include Cash"
              />
              <Divider orientation="horizontal" />
              {enableBulkClosePosition && (
                <Button
                  startIcon={IconName.Close}
                  size={FormControlSizes.Small}
                  onClick={handleCloseAll}
                  data-testid="bulk-close-all-button"
                >
                  <HStack gap="spacing4">
                    <Text>Close All</Text>
                    <HelpIcon tooltip="Close All will bring up the Bulk Close Positions preview dialog for all positions currently present in the blotter." />
                  </HStack>
                </Button>
              )}
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => {
                    positionsMenu.openUpdateBalancesDialog();
                  }}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
